import React, { useState } from 'react';
import { requestOtp, verifyOtp } from '@/api/client';
import AppFooter from '@/components/layout/AppFooter';

interface LoginProps {
  onAuthenticated: () => void;
}

type Step = 'email' | 'otp';

/**
 * The sign-in page is the only page a person sees before they have an
 * account, so it also says what the product is and how it works —
 * the six stages, in the order a drafter meets them, and the three
 * rules the tool keeps (the drafter decides, stages freeze, output is
 * grounded and clean). Copy is kept to what the build actually does;
 * see README "User Manual" for the long form.
 */
const STAGES: Array<{ title: string; text: string }> = [
  {
    title: 'Upload the disclosure',
    text: 'The invention disclosure and anything that goes with it — DOCX, PDF, slides, images. Every document is indexed so later stages can quote it.',
  },
  {
    title: 'Understand it',
    text: 'A structured technical reading of the disclosure, with a gap analysis that says what the inventor has not yet told you.',
  },
  {
    title: 'Frame novelty and inventive step',
    text: 'Novelty and inventive-step statements, and strategy directions for the claim set, each traceable to the disclosure.',
  },
  {
    title: 'Draft the claims',
    text: 'Independent and dependent claims drafted for EP, US or IN practice, quality-checked, edited in place, exported as DOCX.',
  },
  {
    title: 'Draw the figures',
    text: 'Figures with a reference-sign registry — every numeral the description later uses is defined here first.',
  },
  {
    title: 'Write the specification',
    text: 'Sections drafted from the frozen claims and drawings, plus a clause-by-clause detailed description you QC before accepting. Export to filing-ready DOCX or PDF.',
  },
];

const PRINCIPLES: Array<{ title: string; text: string }> = [
  {
    title: 'You decide.',
    text: 'Everything the AI writes is a proposal you read, edit, accept or discard. Nothing enters the document unreviewed.',
  },
  {
    title: 'Stages freeze.',
    text: 'Each stage is frozen before the next builds on it, so the claims a specification supports cannot drift underneath it.',
  },
  {
    title: 'Grounded and clean.',
    text: 'Drafts are written from your disclosure — reference signs come from your drawings and unsupported points are flagged, not invented. Exports carry the document, its title and your firm’s name, nothing else.',
  },
];

// Native HTML elements rather than the shadcn Card/Input/Label/Alert
// wrappers. The .jsx wrappers use React.forwardRef without typed props,
// so a .tsx consumer trips TS2322 (children doesn't exist on
// IntrinsicAttributes). The login screen stays visually consistent
// because it just reuses the same Tailwind tokens (bg-card, border,
// text-muted-foreground, ring-primary, etc.).
export default function Login({ onAuthenticated }: LoginProps) {
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  async function handleRequestOtp(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    if (!email.trim()) {
      setError('Enter your email to continue.');
      return;
    }
    setPending(true);
    try {
      await requestOtp(email.trim());
      setStep('otp');
      setInfo('A 6-digit code has been sent. It expires in 5 minutes.');
    } catch {
      setError('Network error — please try again.');
    } finally {
      setPending(false);
    }
  }

  async function handleVerifyOtp(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!/^\d{6}$/.test(code)) {
      setError('Enter the 6-digit code from your email.');
      return;
    }
    setPending(true);
    try {
      await verifyOtp(email.trim(), code);
      onAuthenticated();
    } catch {
      setError('Invalid or expired code.');
    } finally {
      setPending(false);
    }
  }

  const inputClass =
    'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 ' +
    'text-sm ring-offset-background placeholder:text-muted-foreground ' +
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ' +
    'focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';
  const primaryBtn =
    'inline-flex h-10 w-full items-center justify-center rounded-md bg-primary ' +
    'px-4 text-sm font-medium text-primary-foreground transition-colors ' +
    'hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 ' +
    'focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 ' +
    'disabled:pointer-events-none';
  const ghostBtn =
    'inline-flex h-10 w-full items-center justify-center rounded-md text-sm ' +
    'font-medium text-muted-foreground hover:text-foreground disabled:opacity-50';
  const labelClass = 'text-sm font-medium leading-none';
  const wordmark = { fontFamily: 'Cabinet Grotesk, sans-serif' };

  return (
    <div
      data-testid="login-screen"
      className="min-h-screen flex flex-col bg-background"
    >
      <div className="flex-1 w-full max-w-6xl mx-auto px-4 py-8 lg:py-14 grid gap-10 lg:grid-cols-[minmax(0,1.15fr)_minmax(320px,420px)] lg:items-start">

        {/* ── What this is and how it works ─────────────────────── */}
        <section
          data-testid="login-product-story"
          className="order-2 lg:order-1 space-y-8"
          aria-labelledby="login-product-title"
        >
          <div className="space-y-3">
            <div
              id="login-product-title"
              className="text-3xl font-semibold leading-none tracking-tight"
              style={wordmark}
            >
              Draft Studio
            </div>
            <p className="text-lg text-foreground/90 leading-snug max-w-xl">
              AI-assisted patent drafting, from the invention disclosure to a
              filing-ready specification.
            </p>
            <p className="text-sm text-muted-foreground max-w-xl">
              You upload what the inventor gave you. Draft Studio reads it,
              drafts the claims, figures and specification with you, and
              exports the result for EP, US or IN practice. It works one
              stage at a time; each stage is reviewed and frozen before the
              next one uses it.
            </p>
          </div>

          <div className="space-y-3">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              How it works
            </h2>
            <ol className="space-y-2.5" data-testid="login-stages">
              {STAGES.map((s, i) => (
                <li key={s.title} className="flex gap-3">
                  <span
                    aria-hidden
                    className="mt-0.5 h-6 w-6 shrink-0 rounded-full border border-border bg-card text-[11px] font-semibold flex items-center justify-center text-muted-foreground"
                  >
                    {i + 1}
                  </span>
                  <div className="min-w-0">
                    <div className="text-sm font-medium leading-6">{s.title}</div>
                    <div className="text-sm text-muted-foreground leading-5">{s.text}</div>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          <div className="space-y-3">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Three rules it keeps
            </h2>
            <ul className="grid gap-3 sm:grid-cols-3" data-testid="login-principles">
              {PRINCIPLES.map((p) => (
                <li
                  key={p.title}
                  className="rounded-lg border bg-card px-3.5 py-3 text-sm"
                >
                  <div className="font-medium">{p.title}</div>
                  <div className="mt-1 text-muted-foreground leading-5">{p.text}</div>
                </li>
              ))}
            </ul>
          </div>

          <p className="text-xs text-muted-foreground max-w-xl">
            Access is by invitation from your workspace administrator. Sign in
            with the email the invitation was sent to; a one-time code goes to
            that address and nothing else is needed.
          </p>
        </section>

        {/* ── Sign in ───────────────────────────────────────────── */}
        <div className="order-1 lg:order-2 w-full lg:sticky lg:top-10">
          <div className="w-full rounded-xl border bg-card text-card-foreground shadow">
            <div className="flex flex-col items-center space-y-2 p-6 pb-2 text-center">
              <div
                className="text-2xl font-semibold leading-none tracking-tight"
                style={wordmark}
              >
                Sign in
              </div>
              <div className="text-sm text-muted-foreground">
                Enter your work email — we&apos;ll send you a one-time code.
              </div>
            </div>

            <div className="space-y-4 p-6 pt-4">
              {error && (
                <div
                  role="alert"
                  data-testid="login-error"
                  className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                >
                  {error}
                </div>
              )}
              {info && !error && (
                <div
                  role="status"
                  data-testid="login-info"
                  className="rounded-md border bg-muted px-3 py-2 text-sm text-muted-foreground"
                >
                  {info}
                </div>
              )}

              {step === 'email' && (
                <form onSubmit={handleRequestOtp} className="space-y-4">
                  <div className="space-y-2">
                    <label htmlFor="login-email" className={labelClass}>Email</label>
                    <input
                      id="login-email"
                      data-testid="login-email-input"
                      className={inputClass}
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@yourfirm.com"
                      disabled={pending}
                      autoFocus
                    />
                  </div>
                  <button
                    type="submit"
                    data-testid="login-request-otp-btn"
                    className={primaryBtn}
                    disabled={pending}
                  >
                    {pending ? 'Sending…' : 'Send code'}
                  </button>
                </form>
              )}

              {step === 'otp' && (
                <form onSubmit={handleVerifyOtp} className="space-y-4">
                  <div className="space-y-2">
                    <label htmlFor="login-otp" className={labelClass}>6-digit code</label>
                    <input
                      id="login-otp"
                      data-testid="login-otp-input"
                      className={inputClass}
                      inputMode="numeric"
                      pattern="\d{6}"
                      maxLength={6}
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                      placeholder="123456"
                      disabled={pending}
                      autoFocus
                    />
                    <p className="text-xs text-muted-foreground">Sent to {email}</p>
                  </div>
                  <button
                    type="submit"
                    data-testid="login-verify-btn"
                    className={primaryBtn}
                    disabled={pending}
                  >
                    {pending ? 'Verifying…' : 'Verify'}
                  </button>
                  <button
                    type="button"
                    className={ghostBtn}
                    disabled={pending}
                    onClick={() => {
                      setStep('email');
                      setCode('');
                      setError(null);
                      setInfo(null);
                    }}
                  >
                    Use a different email
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
      <AppFooter includeCompliance={false} />
    </div>
  );
}
